import { useId, useState } from 'react'

import { Input } from '@/components/ui'
import type { StatementSegment, Transaction } from '@/modules/projects'
import { numberToCurrency } from '@/utils'
import { AddRow } from './AddRow'
import { LedgerTable } from './LedgerTable'

// A cycle with nothing to file rows under, so the user names the account by hand. The printed
// balances stay read-only: lukita can only correct one that already belongs to an account entry, and
// this cycle has none. `showBalances` is false when nothing ever read them, so the stored $0.00
// defaults are not printed as if they came off the statement.
export const CycleSummarySection = ({
  segment,
  rows,
  suggestedLast4,
  showBalances,
  onEditAmount,
  onRemove,
  onRestore,
  onAdd,
  onJump,
}: {
  segment: StatementSegment
  rows: Transaction[]
  suggestedLast4: string
  showBalances: boolean
  onEditAmount: (row: Transaction, next: number) => void
  onRemove: (row: Transaction) => void
  onRestore: (row: Transaction) => void
  onAdd: (
    last4: string,
    input: { date: string; description: string; amount: number },
  ) => void
  onJump: (page: number | null, fileUrl: string) => void
}) => {
  const fieldId = useId()
  const [last4, setLast4] = useState(suggestedLast4)
  const net = rows
    .filter((row) => !row.discardedAt)
    .reduce((total, row) => total + row.amount, 0)

  const needsAccount = last4.trim() === ''
  const accountHint = 'Enter the account number first'

  return (
    <div className="rounded-lg border bg-white p-3">
      <p className="text-xs text-muted-foreground">
        {showBalances
          ? 'No account breakdown was read from these pages. Name the account to add the rows it should have.'
          : 'Name the account to add the rows this cycle should have.'}
      </p>

      <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-2 border-y py-2 text-xs">
        <div className="flex items-center gap-2">
          <label htmlFor={`${fieldId}-last4`} className="text-muted-foreground">
            Account
          </label>
          <Input
            id={`${fieldId}-last4`}
            value={last4}
            maxLength={4}
            placeholder="Last 4"
            onChange={(event) => setLast4(event.target.value.trim())}
            className="h-7 w-24 tabular-nums"
          />
        </div>
        {showBalances && (
          <>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Begin</span>
              <span className="tabular-nums text-foreground">
                {segment.startBalance == null
                  ? '--'
                  : numberToCurrency(segment.startBalance)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Net</span>
              <span className="tabular-nums text-foreground">
                {numberToCurrency(net)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">End</span>
              <span className="tabular-nums text-foreground">
                {segment.endBalance == null
                  ? '--'
                  : numberToCurrency(segment.endBalance)}
              </span>
            </div>
          </>
        )}
      </div>

      <AddRow
        onAdd={(input) => onAdd(last4, input)}
        blockedReason={needsAccount ? accountHint : undefined}
      />

      <LedgerTable
        rows={rows}
        emptyMessage="No transactions were found on these pages."
        onEditAmount={onEditAmount}
        onRemove={onRemove}
        onRestore={onRestore}
        onJump={onJump}
      />
    </div>
  )
}
